/**
 * Messages Hooks
 * Jotai atoms and hooks for message and conversation management with infinite scroll
 */

import { useAtom } from "jotai";
import { atomWithInfiniteQuery } from "jotai-tanstack-query";
import { useMemo, useCallback, useRef } from "react";
import {
  messageService,
  type ConversationSummary,
  type ConversationsResponse,
} from "@/services/message.service";
import type { Message } from "@/types/shadow.types";
import { buScopeKey, buScopeParams } from "@/lib/business-unit-scope";
import type { ConversationFilters } from "@/modules/shadow/components/conversation-filters";

/** Which listing an atom reads: the shadow-audit list or every conversation. */
type ConversationSource = "shadow" | "all";

/**
 * Infinite query atom for fetching conversations (shadow-audit list or all conversations)
 */
const createConversationsQueryAtom = (filters?: ConversationFilters, source: ConversationSource = "shadow") => {
  const filterKey = filters
    ? JSON.stringify({
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
        contactSearch: filters.contactSearch,
        conversationIdSearch: filters.conversationIdSearch,
        ruleId: filters.ruleId,
        sortOrder: filters.sortOrder,
        rulesAndKbs: filters.rulesAndKbs,
        responseQuality: filters.responseQuality,
        intents: filters.intents,
      })
    : "no-filters";

  // Only the Shadow Audit list is brand-scoped; the simulator's picker stays account-wide.
  return atomWithInfiniteQuery((get) => ({
    queryKey: ["conversations", source, filterKey, source === "shadow" ? buScopeKey(get) : "account"],
    queryFn: async ({ pageParam }: { pageParam?: number }) => {
      return source === "all"
        ? messageService.getAllConversations(20, pageParam, filters)
        : messageService.getConversationsWithShadow(20, pageParam, filters, buScopeParams(get));
    },
    getNextPageParam: (lastPage: ConversationsResponse) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as number | undefined,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  }));
};

/**
 * Cache for conversation message query atoms to prevent recreation
 */
const conversationMessageAtomsCache = new Map<
  number,
  ReturnType<typeof atomWithInfiniteQuery>
>();

/**
 * Infinite query atom for fetching messages for a conversation
 */
const getConversationMessagesQueryAtom = (conversationId: number) => {
  if (!conversationMessageAtomsCache.has(conversationId)) {
    conversationMessageAtomsCache.set(
      conversationId,
      atomWithInfiniteQuery(() => ({
        queryKey: ["conversations", conversationId, "messages"],
        queryFn: async ({ pageParam }: { pageParam?: string }) => {
          return messageService.getConversationMessages(
            conversationId,
            50,
            pageParam,
          );
        },
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        initialPageParam: undefined as string | undefined,
        enabled: conversationId > 0,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      })),
    );
  }
  return conversationMessageAtomsCache.get(conversationId)!;
};

/**
 * Hook to fetch conversations with shadow metadata (with infinite scroll)
 */
export function useConversationsWithShadow(filters?: ConversationFilters) {
  return useConversationList(filters, "shadow");
}

/**
 * Hook to fetch every conversation of the account (with infinite scroll) — the simulator's picker.
 */
export function useAllConversations(filters?: ConversationFilters) {
  return useConversationList(filters, "all");
}

function useConversationList(filters: ConversationFilters | undefined, source: ConversationSource) {
  // Extract complex expressions to separate variables for dependency array
  const startDateISO = filters?.startDate?.toISOString();
  const endDateISO = filters?.endDate?.toISOString();
  const rulesAndKbsJSON = JSON.stringify(filters?.rulesAndKbs);
  const responseQualityJSON = JSON.stringify(filters?.responseQuality);

  const queryAtom = useMemo(
    () => createConversationsQueryAtom(filters, source),
    [
      filters,
      source,
      startDateISO,
      endDateISO,
      rulesAndKbsJSON,
      responseQualityJSON,
    ],
  );
  const [query] = useAtom(queryAtom);

  // Flatten all pages into a single array
  const conversations: ConversationSummary[] = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.conversations);
  }, [query.data]);

  const hasMore =
    query.data?.pages[query.data.pages.length - 1]?.hasMore ?? false;
  const totalCount = query.data?.pages[0]?.totalCount ?? 0;
  const isLoading = query.isLoading;
  const isError = query.isError;
  const isFetchingNextPage = query.isFetchingNextPage;

  // Use ref to avoid dependency on query object
  const queryRef = useRef(query);
  queryRef.current = query;

  const fetchNextPage = useCallback(() => {
    if (hasMore && !isFetchingNextPage) {
      queryRef.current.fetchNextPage();
    }
  }, [hasMore, isFetchingNextPage]);

  return {
    data: conversations,
    totalCount,
    isLoading,
    isError,
    hasMore,
    isFetchingNextPage,
    fetchNextPage,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch messages for a conversation (with infinite scroll)
 */
export function useConversationMessages(conversationId?: number) {
  // Get or create atom for this conversationId, memoized to prevent recreation
  const atom = useMemo(() => {
    if (!conversationId || conversationId <= 0) {
      // Return a disabled dummy atom to satisfy React hooks rules
      return atomWithInfiniteQuery(() => ({
        queryKey: ["conversations", 0, "messages", "dummy"],
        queryFn: async () => ({
          messages: [],
          hasMore: false,
          nextCursor: undefined,
        }),
        getNextPageParam: () => undefined,
        initialPageParam: undefined as string | undefined,
        enabled: false,
      }));
    }
    return getConversationMessagesQueryAtom(conversationId);
  }, [conversationId]);

  // Always call useAtom hook (React hooks must be called unconditionally)
  const [query] = useAtom(atom);

  // Flatten all pages into a single array
  const messages: Message[] = useMemo(() => {
    if (!conversationId || !query.data) return [];
    const data = query.data as { pages?: Array<{ messages: Message[] }> };
    if (!data.pages) return [];
    return data.pages.flatMap((page) => page.messages);
  }, [conversationId, query.data]);

  const data = query.data as
    | { pages?: Array<{ hasMore?: boolean }> }
    | undefined;
  const hasMore = data?.pages?.[data.pages.length - 1]?.hasMore ?? false;
  const isLoading = query.isLoading;
  const isError = query.isError;
  const isFetchingNextPage = query.isFetchingNextPage;

  // Use ref to store the latest query.fetchNextPage to avoid dependency issues
  const queryRef = useRef(query);
  queryRef.current = query;

  const fetchNextPage = useCallback(() => {
    if (hasMore && !isFetchingNextPage && conversationId) {
      queryRef.current.fetchNextPage();
    }
  }, [hasMore, isFetchingNextPage, conversationId]);

  // Return empty state if no conversationId provided
  if (!conversationId || conversationId <= 0) {
    return {
      data: [],
      isLoading: false,
      isError: false,
      hasMore: false,
      isFetchingNextPage: false,
      fetchNextPage: () => {},
      refetch: async () => ({ data: { pages: [] } }),
    } as const;
  }

  return {
    data: messages,
    isLoading,
    isError,
    hasMore,
    isFetchingNextPage,
    fetchNextPage,
    refetch: query.refetch,
  };
}
